import { Tag } from 'antd';
import type { ProdutoLocalEstoque } from '@/features/produto-local-estoque/types/ProdutoLocalEstoque';
import type { ProdutoBodyEstoque, ProdutoBodyLote } from '@/features/produto/types/ProdutoBody';

const formatQuantidade = (quantidade: number): string =>
    quantidade.toLocaleString('pt-BR', { maximumFractionDigits: 4 });

export function ProdutoTabEstoqueLoteResumo({
    locais,
    lotes,
    produtoEstoque,
}: {
    locais: ProdutoLocalEstoque[];
    lotes: ProdutoBodyLote[];
    produtoEstoque: ProdutoBodyEstoque[];
}) {
    const lotesVisiveis = lotes.filter((lote) => lote.excluir !== 'S');

    const esperadoPorLocal = new Map<number, number>();

    for (const estoque of produtoEstoque) {
        esperadoPorLocal.set(
            estoque.pre_estoque_idpk,
            (esperadoPorLocal.get(estoque.pre_estoque_idpk) ?? 0) +
                (estoque.pre_estoque_atual ?? 0),
        );
    }

    if (esperadoPorLocal.size === 0) {
        return null;
    }

    return (
        <section
            aria-label='Conferência dos estoques por lote'
            className='mt-3 flex flex-col gap-3'
        >
            <strong>Totalizador de estoques por local</strong>

            <div className='grid gap-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'>
                {[...esperadoPorLocal].map(([localId, esperado]) => {
                    const local = locais.find((item) => item.est_idpk === localId);

                    const estoquesLotes = lotesVisiveis.map((lote) => ({
                        lote,
                        quantidade: lote.estoque?.find((item) => item.ple_estoque_idpk === localId)
                            ?.ple_estoque_atual,
                    }));

                    const total = estoquesLotes.reduce(
                        (soma, item) => soma + (item.quantidade ?? 0),
                        0,
                    );

                    const diferenca = esperado - total;

                    const quantidadePendente = estoquesLotes.some(
                        (item) => typeof item.quantidade !== 'number',
                    );

                    const divergente = quantidadePendente || Math.abs(diferenca) > 0.0001;

                    return (
                        <div
                            className={`rounded-md border p-3 ${divergente ? 'border-red-200 bg-red-50 text-red-700' : 'border-green-200 bg-green-50 text-green-700'}`}
                            key={localId}
                        >
                            <div className='mb-2 flex flex-wrap items-center justify-between gap-2'>
                                <strong>{local?.est_descricao ?? `Local ${localId}`}</strong>
                                <Tag className='m-0' color={divergente ? 'red' : 'green'}>
                                    {divergente ? 'Estoque divergente' : 'Estoque conferido'}
                                </Tag>
                            </div>

                            <div className='flex flex-wrap gap-x-6 gap-y-1'>
                                <span>
                                    Estoque esperado: <b>{formatQuantidade(esperado)}</b>
                                </span>
                                <span>
                                    Total nos lotes: <b>{formatQuantidade(total)}</b>
                                </span>
                                <span>
                                    {Math.abs(diferenca) > 0.0001
                                        ? diferenca > 0
                                            ? 'Falta: '
                                            : 'Excesso: '
                                        : 'Diferença: '}
                                    <b>
                                        {formatQuantidade(
                                            Math.abs(diferenca) > 0.0001 ? Math.abs(diferenca) : 0,
                                        )}
                                    </b>
                                </span>
                            </div>

                            {quantidadePendente && (
                                <div className='mt-2'>
                                    Informe as quantidades pendentes nos lotes.
                                </div>
                            )}

                            <div className='mt-2 flex flex-wrap items-center gap-1'>
                                <span>Lotes:</span>
                                {estoquesLotes.length === 0 ? (
                                    <span>Nenhum lote cadastrado</span>
                                ) : (
                                    estoquesLotes.map(({ lote, quantidade }, index) => (
                                        <Tag className='m-0' key={lote.key}>
                                            {lote.prl_chave || `Lote ${index + 1}`}:{' '}
                                            {quantidade == null
                                                ? 'Não informado'
                                                : formatQuantidade(quantidade)}
                                        </Tag>
                                    ))
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}
